
import { useMemo } from 'react';

interface PriceHistory {
  date: string;
  price: number;
}

interface PriceHistoryChartProps {
  data: PriceHistory[];
  lowestPrice: number;
  highestPrice: number;
  currentPrice: number;
}

const PriceHistoryChart = ({ data, lowestPrice, highestPrice, currentPrice }: PriceHistoryChartProps) => {
  const chartData = useMemo(() => {
    if (data.length === 0) return { points: '', area: '', labels: [] };

    const width = 100;
    const height = 100;
    const padding = 10;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const minPrice = Math.min(...data.map(d => d.price)) * 0.95;
    const maxPrice = Math.max(...data.map(d => d.price)) * 1.05;
    const priceRange = maxPrice - minPrice;

    const points = data.map((d, i) => {
      const x = padding + (i / (data.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((d.price - minPrice) / priceRange) * chartHeight;
      return `${x},${y}`;
    }).join(' ');

    const areaPoints = [
      `${padding},${padding + chartHeight}`,
      ...data.map((d, i) => {
        const x = padding + (i / (data.length - 1)) * chartWidth;
        const y = padding + chartHeight - ((d.price - minPrice) / priceRange) * chartHeight;
        return `${x},${y}`;
      }),
      `${padding + chartWidth},${padding + chartHeight}`,
    ].join(' ');

    const labels = data.filter((_, i) => i % Math.ceil(data.length / 5) === 0 || i === data.length - 1);

    return { points, area: areaPoints, labels, minPrice, maxPrice };
  }, [data]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const priceStats = useMemo(() => {
    const prices = data.map(d => d.price);
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    return {
      average: Math.round(avg),
      lowest: Math.min(...prices),
      highest: Math.max(...prices),
    };
  }, [data]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Price History</h3>
        <div className="flex items-center gap-4 text-sm">
          <button className="px-3 py-1 rounded-full bg-teal-100 text-teal-700 font-medium cursor-pointer whitespace-nowrap">6M</button>
          <button className="px-3 py-1 rounded-full text-gray-500 hover:bg-gray-100 cursor-pointer whitespace-nowrap">1Y</button>
          <button className="px-3 py-1 rounded-full text-gray-500 hover:bg-gray-100 cursor-pointer whitespace-nowrap">All</button>
        </div>
      </div>

      {/* Price Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1">Lowest</p>
          <p className="text-lg font-bold text-green-600">${priceStats.lowest}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1">Average</p>
          <p className="text-lg font-bold text-gray-700">${priceStats.average}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1">Highest</p>
          <p className="text-lg font-bold text-red-500">${priceStats.highest}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="relative h-48 mb-4">
        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
          {/* Grid lines */}
          <line x1="10" y1="25" x2="90" y2="25" stroke="#e5e7eb" strokeWidth="0.3" strokeDasharray="2,2" />
          <line x1="10" y1="50" x2="90" y2="50" stroke="#e5e7eb" strokeWidth="0.3" strokeDasharray="2,2" />
          <line x1="10" y1="75" x2="90" y2="75" stroke="#e5e7eb" strokeWidth="0.3" strokeDasharray="2,2" />
          
          {/* Area fill */}
          <polygon
            points={chartData.area}
            fill="url(#gradient)"
            opacity="0.3"
          />
          
          {/* Line */}
          <polyline
            points={chartData.points}
            fill="none"
            stroke="#14b8a6"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Data points */}
          {data.map((d, i) => {
            const x = 10 + (i / (data.length - 1)) * 80;
            const minPrice = Math.min(...data.map(p => p.price)) * 0.95;
            const maxPrice = Math.max(...data.map(p => p.price)) * 1.05;
            const y = 10 + 80 - ((d.price - minPrice) / (maxPrice - minPrice)) * 80;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="1.5"
                fill="#14b8a6"
                className="hover:r-3 transition-all"
              />
            );
          })}
          
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Date labels */}
      <div className="flex justify-between text-xs text-gray-400 px-2">
        {chartData.labels?.map((item, i) => (
          <span key={i}>{formatDate(item.date)}</span>
        ))}
      </div>

      {/* Current price indicator */}
      <div className="mt-6 flex items-center justify-between p-4 bg-teal-50 rounded-lg">
        <div>
          <p className="text-sm text-gray-600">Current Price</p>
          <p className="text-2xl font-bold text-gray-900">${currentPrice}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">vs. Average</p>
          <p className={`text-lg font-semibold ${currentPrice < priceStats.average ? 'text-green-600' : 'text-red-500'}`}>
            {currentPrice < priceStats.average ? '-' : '+'}${Math.abs(currentPrice - priceStats.average)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriceHistoryChart;
